import React from 'react';
import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Panel, StatCard, StatusPill, EmptyState, ProgressBar } from '../ui';
import { formatCurrency } from './utils';

const healthStatus = (completionRate) => {
  if (completionRate >= 80) return { status: 'Done', label: 'On Track' };
  if (completionRate >= 50) return { status: 'At Risk', label: 'At Risk' };
  return { status: 'Blocked', label: 'Critical' };
};

const ProjectHealthDashboard = ({ projectMetrics = [], currencyCode = 'USD' }) => {
  const onTrack = projectMetrics.filter((p) => (p.completionRate || 0) >= 80).length;
  const atRisk = projectMetrics.filter(
    (p) => (p.completionRate || 0) >= 50 && (p.completionRate || 0) < 80
  ).length;
  const critical = projectMetrics.filter((p) => (p.completionRate || 0) < 50).length;

  return (
    <div className="space-y-6" data-testid="project-health-dashboard">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard icon={CheckCircle} tone="green" label="On Track" value={onTrack} />
        <StatCard icon={AlertTriangle} tone="amber" label="At Risk" value={atRisk} />
        <StatCard icon={XCircle} tone="red" label="Critical" value={critical} />
      </div>

      <Panel>
        <h3 className="text-lg font-semibold mb-4">Project Status Details</h3>
        {projectMetrics.length === 0 ? (
          <EmptyState title="No projects" description="Project health metrics will appear here." />
        ) : (
          <div className="space-y-3">
            {projectMetrics.map((project) => {
              const health = healthStatus(project.completionRate || 0);
              return (
                <div
                  key={project.id}
                  className="p-4 bg-secondary-50 rounded-lg"
                  data-testid={`project-health-${project.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-secondary-900">{project.name}</h4>
                    <StatusPill status={health.status} label={health.label} />
                  </div>
                  <ProgressBar value={project.completionRate || 0} className="mb-3" />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-secondary-600">Tasks:</span>
                      <span className="ml-1 font-medium">
                        {project.completedTasks}/{project.totalTasks}
                      </span>
                    </div>
                    <div>
                      <span className="text-secondary-600">Overdue:</span>
                      <span className="ml-1 font-medium text-red-600">
                        {project.overdueTasks}
                      </span>
                    </div>
                    <div>
                      <span className="text-secondary-600">Efficiency:</span>
                      <span className="ml-1 font-medium">
                        {(project.efficiency || 0).toFixed(1)}%
                      </span>
                    </div>
                    <div>
                      <span className="text-secondary-600">Revenue:</span>
                      <span className="ml-1 font-medium text-green-600">
                        {formatCurrency(project.revenue, currencyCode)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
};

export default ProjectHealthDashboard;
